import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Video } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('kaplie_token', response.data.token);
      localStorage.setItem('kaplie_user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success('Logged in successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-inter flex items-center justify-center px-6 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.05] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-violet-900/[0.08] rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" data-testid="back-to-home-link">
          <button className="flex items-center gap-2 text-zinc-500 hover:text-white mb-8 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>
        </Link>

        <div className="backdrop-blur-2xl bg-black/60 border border-white/[0.08] rounded-2xl p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          {/* Brand mark */}
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.2)]">
              <Video className="w-4 h-4 text-black" />
            </div>
            <span className="font-satoshi font-bold text-lg tracking-tight">Kaplie</span>
          </div>

          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-2">Welcome back</p>
          <h1 className="font-satoshi font-bold text-3xl tracking-tight mb-2" data-testid="login-heading">Sign in</h1>
          <p className="text-zinc-500 mb-8 text-sm">Log in to start your video calls</p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-xs uppercase tracking-wider font-semibold text-zinc-400 mb-2">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="w-full bg-black/40 border border-white/[0.08] text-white rounded-xl px-4 py-3 focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/30 focus:outline-none transition-all placeholder:text-zinc-600"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs uppercase tracking-wider font-semibold text-zinc-400 mb-2">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full bg-black/40 border border-white/[0.08] text-white rounded-xl px-4 py-3 focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/30 focus:outline-none transition-all placeholder:text-zinc-600"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full py-3.5 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="text-center mt-5">
            <Link to="/forgot-password" className="text-sm text-zinc-500 hover:text-[#D4AF37] transition-colors" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          <div className="h-px bg-white/[0.06] my-6" />

          <p className="text-center text-zinc-500 text-sm">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#D4AF37] hover:underline font-medium" data-testid="signup-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
